/**
 * Workflow tables (AMO-299): the tables a step's result carries beside its
 * text, shared by the server (which builds them from the sheets an Intake
 * step reads and the rows an Ingestion step reads) and the web run panel
 * (which shows them and downloads them as CSV).
 *
 * A table is named columns and rows, every row a cell per column. Cells are
 * text, numbers or empty, kept raw (a number is not formatted) so a later
 * step compares and exports the value itself. Every table is held within
 * the limits below; rows past them are counted in `omittedRows`, never
 * dropped silently.
 *
 * Types are structural so the module needs nothing but itself; the wire
 * schemas live in `@amoreg/contracts` (`WorkflowStepTable`).
 *
 * @module workflowTables
 */

export type WorkflowTableCellLike = string | number | null;

export interface WorkflowTableLike {
  readonly name: string;
  readonly columns: ReadonlyArray<string>;
  readonly rows: ReadonlyArray<ReadonlyArray<WorkflowTableCellLike>>;
  readonly omittedRows?: number | undefined;
}

/** The wire limits (`WORKFLOW_LIMITS.stepTables`, `tableRows`, `tableColumns`, `tableCellText`, `stepTableCells`). */
export const MAX_STEP_TABLES = 20;
export const MAX_TABLE_ROWS = 2_000;
export const MAX_TABLE_COLUMNS = 50;
export const MAX_TABLE_CELL_TEXT = 1_000;
export const MAX_STEP_TABLE_CELLS = 40_000;
/** The most of a table's name kept (an Excel sheet name is 31; a heading may be longer). */
const MAX_TABLE_NAME = 120;

const cellOf = (value: unknown): WorkflowTableCellLike => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "boolean") return value ? "TRUE" : "FALSE";
  const text = String(value);
  return text.trim() === "" ? null : text.slice(0, MAX_TABLE_CELL_TEXT);
};

const isEmptyRow = (row: ReadonlyArray<unknown>) => row.every((cell) => cellOf(cell) === null);

/**
 * Column names from a header row: each cell's text trimmed, a blank one
 * "Column N" (its position, from 1), and a repeated one numbered so every
 * name is distinct ("Price", "Price 2").
 */
export function tableColumnNames(header: ReadonlyArray<unknown>): ReadonlyArray<string> {
  const taken = new Set<string>();
  return header.map((raw, index) => {
    const base = (cellOf(raw) === null ? "" : String(cellOf(raw)).replace(/\s+/gu, " ").trim().slice(0, 80)) || `Column ${index + 1}`;
    let name = base;
    for (let n = 2; taken.has(name.toLowerCase()); n += 1) name = `${base} ${n}`;
    taken.add(name.toLowerCase());
    return name;
  });
}

/**
 * A table from a grid of cells, as a sheet or a CSV file lays it out: the
 * first row with anything in it is the header, the rows after it the data.
 * Empty rows are dropped, and so are columns empty from the header down. Null
 * when the grid holds no header at all.
 */
export function tableFromGrid(name: string, grid: ReadonlyArray<ReadonlyArray<unknown>>): WorkflowTableLike | null {
  const start = grid.findIndex((row) => !isEmptyRow(row));
  if (start < 0) return null;
  const body = grid.slice(start).filter((row) => !isEmptyRow(row));
  const width = body.reduce((most, row) => Math.max(most, row.length), 0);
  // Empty columns go first, so a sheet whose data starts far to the right still keeps it.
  const keep = Array.from({ length: width }, (_, col) => col)
    .filter((col) => body.some((row) => cellOf(row[col]) !== null))
    .slice(0, MAX_TABLE_COLUMNS);
  const [header = [], ...data] = body.map((row) => keep.map((col) => cellOf(row[col])));
  return {
    name: name.replace(/\s+/gu, " ").trim().slice(0, MAX_TABLE_NAME) || "Table",
    columns: tableColumnNames(header),
    rows: data.slice(0, MAX_TABLE_ROWS),
    ...(data.length > MAX_TABLE_ROWS ? { omittedRows: data.length - MAX_TABLE_ROWS } : {}),
  };
}

/**
 * The tables one step's result keeps: at most MAX_STEP_TABLES, and rows
 * only while the step's cells stay within MAX_STEP_TABLE_CELLS (the rows
 * a table loses for that are counted in its `omittedRows`). Names are made
 * distinct, as sheet names must be.
 */
export function boundStepTables(tables: ReadonlyArray<WorkflowTableLike>): ReadonlyArray<WorkflowTableLike> {
  let room = MAX_STEP_TABLE_CELLS;
  const names = new Set<string>();
  return tables.slice(0, MAX_STEP_TABLES).map((table) => {
    const width = Math.max(1, table.columns.length);
    const fits = Math.max(0, Math.min(table.rows.length, Math.floor(room / width)));
    room -= fits * width;
    let name = table.name;
    for (let n = 2; names.has(name.toLowerCase()); n += 1) name = `${table.name} (${n})`;
    names.add(name.toLowerCase());
    const omitted = (table.omittedRows ?? 0) + (table.rows.length - fits);
    return {
      name,
      columns: table.columns,
      // Every cell within the cell limit, whoever built the table (an Ingestion reply too).
      rows: table.rows.slice(0, fits).map((row) => row.map((cell) => (typeof cell === "string" ? cell.slice(0, MAX_TABLE_CELL_TEXT) : cell))),
      ...(omitted > 0 ? { omittedRows: omitted } : {}),
    };
  });
}

/** A cell as text: a number as it is (never in exponent form for ordinary values), empty as "". */
export const tableCellText = (cell: WorkflowTableCellLike): string => (cell === null ? "" : String(cell));

const csvField = (text: string) => (/[",\r\n]/u.test(text) || /^\s|\s$/u.test(text) ? `"${text.replace(/"/gu, '""')}"` : text);

/**
 * Whether a spreadsheet would start a formula at this text: `=`, `+`, `-` or
 * `@` first, past any white space, control characters or byte-order mark it
 * skips (`+1+1` and `-1+SUM(A1)` are formulas to it, not numbers).
 */
const startsFormula = (text: string): boolean => {
  // A leading tab or carriage return is itself a trigger (OWASP's list).
  if (text.startsWith("\t") || text.startsWith("\r")) return true;
  let at = 0;
  while (at < text.length && (text.charCodeAt(at) < 33 || /\s/u.test(text[at]!) || text.charCodeAt(at) === 0xfeff)) at += 1;
  return at < text.length && "=+-@".includes(text[at]!);
};

/**
 * A table as CSV (RFC 4180, CRLF lines), header first. A text cell a
 * spreadsheet would read as a formula (`startsFormula`) is prefixed with an
 * apostrophe, so a downloaded file cannot run one. Number cells are typed as
 * numbers, so they are written as they are.
 */
export function tableToCsv(table: WorkflowTableLike): string {
  const field = (cell: WorkflowTableCellLike) => {
    const text = tableCellText(cell);
    return csvField(typeof cell === "string" && startsFormula(text) ? `'${text}` : text);
  };
  return [table.columns.map((name) => field(name)), ...table.rows.map((row) => row.map(field))].map((line) => line.join(",")).join("\r\n");
}

const markdownCell = (cell: WorkflowTableCellLike) =>
  tableCellText(cell).replace(/\\/gu, "\\\\").replace(/\|/gu, "\\|").replace(/\r?\n/gu, " ");

/**
 * A table as a Markdown table for a step's text result (what a Prompt step
 * reads), its first `maxRows` rows, and a line naming the rows left out.
 */
export function tableToMarkdown(table: WorkflowTableLike, maxRows = 200): string {
  if (table.columns.length === 0) return `${table.name}: (no columns)`;
  const shown = table.rows.slice(0, maxRows);
  const left = table.rows.length - shown.length + (table.omittedRows ?? 0);
  return [
    `| ${table.columns.map(markdownCell).join(" | ")} |`,
    `| ${table.columns.map(() => "---").join(" | ")} |`,
    ...shown.map((row) => `| ${table.columns.map((_, col) => markdownCell(row[col] ?? null)).join(" | ")} |`),
    ...(left > 0 ? ["", `(${left} more ${left === 1 ? "row" : "rows"} not shown)`] : []),
  ].join("\n");
}
